import { useMemo } from "react";
import type { PerformanceReportRow } from "@/types/reporting";
import { compareNames } from "@/utils/sortOptions";
import { TEXT } from "@/constants/text";

export interface MultiFilterState {
  browsers: string[];
  devices: string[];
  osTypes: string[];
}

export interface BarItem {
  ctr: number | null;
  impressions: number;
  label: string;
}

export interface SelectOption {
  label: string;
  value: string;
}

export interface FilterOptions {
  browsers: SelectOption[];
  devices: SelectOption[];
  osTypes: SelectOption[];
}

const BAR_TOP_N = 8;
const OTHER_LABEL = TEXT.campaignOverview.labels.other;

// Every row maps to one value per dimension; missing values share the
// "Other" bucket so the filters, option lists and bars all agree on it.
const getDimensions = (row: PerformanceReportRow) => ({
  browser: row.browser ?? OTHER_LABEL,
  device: row.openrtb_device_type ?? OTHER_LABEL,
  os: row.os_type ?? OTHER_LABEL,
});

// An empty filter array means "no restriction".
const matches = (selected: string[], value: string): boolean =>
  selected.length === 0 || selected.includes(value);

// A–Z, with the "Other" bucket always last.
const toSortedValues = (values: Set<string>): string[] =>
  [...values].sort((valueA, valueB) => {
    if (valueA === OTHER_LABEL) return 1;
    if (valueB === OTHER_LABEL) return -1;
    return compareNames(valueA, valueB);
  });

const toOptions = (values: string[]): SelectOption[] =>
  values.map((value) => ({ label: value, value }));

const passesFilter = (
  row: PerformanceReportRow,
  { browsers, devices, osTypes }: MultiFilterState
): boolean => {
  const { browser, device, os } = getDimensions(row);
  return (
    matches(devices, device) &&
    matches(osTypes, os) &&
    matches(browsers, browser)
  );
};

// Collects each dimension's values, cascading: OS types are scoped to the
// selected devices so impossible combos (e.g. Mobile Phone + macOS) can't be
// offered, and browsers to the selected devices and OS types. Devices are
// never scoped, so the user can always recover the filter state.
const collectValues = (
  rows: PerformanceReportRow[],
  { devices, osTypes }: Pick<MultiFilterState, "devices" | "osTypes">
): MultiFilterState => {
  const deviceValues = new Set<string>();
  const osValues = new Set<string>();
  const browserValues = new Set<string>();

  rows.forEach((row) => {
    const { browser, device, os } = getDimensions(row);
    deviceValues.add(device);
    if (!matches(devices, device)) return;
    osValues.add(os);
    if (matches(osTypes, os)) browserValues.add(browser);
  });

  return {
    browsers: toSortedValues(browserValues),
    devices: toSortedValues(deviceValues),
    osTypes: toSortedValues(osValues),
  };
};

const NO_FILTERS: MultiFilterState = { browsers: [], devices: [], osTypes: [] };

// Every value in each dimension across all rows — used to initialise and
// reset filters to the "all selected" state.
export const computeAllOptions = (
  rows: PerformanceReportRow[]
): MultiFilterState => collectValues(rows, NO_FILTERS);

export const useDeviceBrowserData = (
  rows: PerformanceReportRow[],
  filters: MultiFilterState,
  leader: string | null = null
): {
  allRowOptions: MultiFilterState;
  bars: BarItem[];
  filterOptions: FilterOptions;
} => {
  const filterOptions = useMemo((): FilterOptions => {
    const { browsers, devices, osTypes } = collectValues(rows, {
      devices: filters.devices,
      osTypes: filters.osTypes,
    });
    return {
      browsers: toOptions(browsers),
      devices: toOptions(devices),
      osTypes: toOptions(osTypes),
    };
  }, [rows, filters.devices, filters.osTypes]);

  const bars = useMemo((): BarItem[] => {
    const browserMap = new Map<
      string,
      { clicks: number; impressions: number }
    >();

    rows.forEach((row) => {
      if (!passesFilter(row, filters)) return;
      const { browser: key } = getDimensions(row);
      const prev = browserMap.get(key) ?? { clicks: 0, impressions: 0 };
      browserMap.set(key, {
        clicks: prev.clicks + (row.clicks ?? 0),
        impressions: prev.impressions + (row.impressions ?? 0),
      });
    });

    const ranked = [...browserMap.entries()].sort(
      ([, dataA], [, dataB]) => dataB.impressions - dataA.impressions
    );
    // The browser the tile names is always charted, even when it ranks below
    // the top N by impressions (as long as the active filters leave it any).
    const leaderEntry = leader
      ? ranked.slice(BAR_TOP_N).find(([label]) => label === leader)
      : undefined;
    const visible = leaderEntry
      ? [...ranked.slice(0, BAR_TOP_N), leaderEntry]
      : ranked.slice(0, BAR_TOP_N);

    return visible.map(([label, { clicks, impressions }]) => ({
      ctr: impressions > 0 ? clicks / impressions : null,
      impressions,
      label,
    }));
  }, [rows, filters, leader]);

  const allRowOptions = useMemo(() => computeAllOptions(rows), [rows]);

  return { allRowOptions, bars, filterOptions };
};
